/**
 * The Home atlas slot's DATA, separated from its drawing (Story #5648, C5).
 *
 * screen-C5-accept-invitation.html draws, in the right column above "Suggested
 * next steps":
 *
 *   <div class="card card-pad">
 *     <div class="card-h">🗺 My inventions</div>
 *     <div class="card-sub">6 inventions · 50 documents analysed
 *                           · 1 scored overlap group · 2 watched regions</div>
 *     <div class="mapwrap mt8" style="padding:6px">
 *       <svg viewBox="0 0 280 150" …>  … six ellipses …  </svg></div>
 *     <a …>Open the atlas →</a>
 *   </div>
 *
 * Everything here is pure, so the two things that go wrong with a summary
 * line — a number that disagrees with the picture beside it, and a number
 * invented when nothing has been computed — are caught by vitest against a
 * fixture rather than found on the deployed screen.
 *
 * WHY A SEPARATE MODULE. The slot draws the SAME footprints as
 * MyInventionsAtlas, from the same payload, through the same exported geometry
 * (lib/whitespace's computeGroupFootprints / makeAtlasProjector, and
 * atlasGeometry's star points). Nothing about the shapes is re-derived here;
 * only the viewport is smaller. Keeping the counting in a pure module means a
 * test can prove the slot's sub-line and the atlas's own roster are reading one
 * set of numbers, without rendering either.
 */
import type { Project } from '../../types'
import type { PortfolioMapResponse, CachedOverlapResponse, PortfolioWatch } from '../../types/whitespace'

/** The C5 slot's canvas. The atlas's own VIEWPORT is 900×560; this is the
 *  figure's 280×150 with a proportionally smaller padding, so the same
 *  fit-to-bounds projector lays the same shapes out in a smaller box. */
export const SLOT_VIEWPORT = { width: 280, height: 150, padding: 14 }

/**
 * The inventions the slot may draw and count: the caller's OWN, not archived.
 *
 * This is the same rule MyInventionsAtlas, ProjectsTable and HomeDashboard all
 * use (`!shared_with_me && status !== 'archived'`). A project shared IN is not
 * one of "your inventions" — the atlas says so and excludes it from the map, so
 * counting it here would put a number on Home that the atlas contradicts one
 * click later.
 */
export function ownedInventions(projects: Project[]): Project[] {
  return projects.filter((p) => !p.shared_with_me && p.status !== 'archived')
}

export interface AtlasSlotCounts {
  inventions: number
  /** null when the recommendations profile has not been computed — the
   *  sub-line then omits the clause rather than claiming zero. */
  documentsAnalysed: number | null
  /** null when no overlap run is cached. Zero is a real answer — a run that
   *  found nothing — and must not be confused with "never computed". */
  overlapGroups: number | null
  /** null when the watch list has not loaded. */
  watchedRegions: number | null
}

export function atlasSlotCounts(input: {
  projects: Project[]
  documentCount?: number | null
  overlap?: Pick<CachedOverlapResponse, 'groups' | 'cached'> | null
  watches?: PortfolioWatch[] | null
}): AtlasSlotCounts {
  return {
    inventions: ownedInventions(input.projects).length,
    documentsAnalysed: input.documentCount ?? null,
    // `cached` is the field that separates "a run found nothing" (0) from
    // "no run has ever been computed" (null). Reading only `groups.length`
    // would print "0 scored overlap groups" for a portfolio that has never
    // been scored, which is a claim the data does not support.
    overlapGroups: input.overlap?.cached ? (input.overlap.groups ?? []).length : null,
    watchedRegions: input.watches ? input.watches.length : null,
  }
}

/**
 * The figure's sub-line, built from whatever is actually known.
 *
 * The figure reads "6 inventions · 50 documents analysed · 1 scored overlap
 * group · 2 watched regions". A clause whose count is UNKNOWN is dropped, not
 * printed as 0 — "0 scored overlap groups" is a claim that a run happened and
 * found nothing, which is a different and possibly false statement from "no run
 * has been cached". Singular/plural is respected because the figure's own line
 * says "1 scored overlap group".
 */
export function atlasSlotSubLine(c: AtlasSlotCounts): string {
  const parts: string[] = [`${c.inventions} invention${c.inventions === 1 ? '' : 's'}`]
  if (c.documentsAnalysed !== null) {
    parts.push(`${c.documentsAnalysed} document${c.documentsAnalysed === 1 ? '' : 's'} analysed`)
  }
  if (c.overlapGroups !== null) {
    parts.push(`${c.overlapGroups} scored overlap group${c.overlapGroups === 1 ? '' : 's'}`)
  }
  if (c.watchedRegions !== null) {
    parts.push(`${c.watchedRegions} watched region${c.watchedRegions === 1 ? '' : 's'}`)
  }
  return parts.join(' · ')
}

/**
 * Which invention ids the slot actually PLOTS.
 *
 * Identical to the atlas's own rule: the server's member list intersected with
 * what the caller owns. The atlas's comment for it is the reason — even if a
 * granted project ever reached the layout it is not one of "your inventions"
 * and must not be drawn.
 */
export function slotPlottedIds(
  map: Pick<PortfolioMapResponse, 'member_project_ids'> | null | undefined,
  projects: Project[],
): string[] {
  const ownedIds = ownedInventions(projects).map((p) => p.project_id)
  return (map?.member_project_ids ?? []).filter((id) => ownedIds.includes(id))
}
